import type { ItemFieldEntry } from '@cheqroom/api';
import {
	ItemInfo as SharedItemInfo,
	type ItemInfoField,
	type ItemInfoItem,
	selectFieldDefs,
	type StackProps,
} from '@cheqroom/components';
import { type FC, type PropsWithChildren, useMemo } from 'react';

import useItemFields, { type UseItemFieldsHook } from '../../../hooks/item-fields/use-item-fields';
import { useItemFieldsConfig } from '../../../hooks/item-fields/use-item-fields-config';
import { useItemInfoFieldDefs } from '../../../hooks/item-fields/use-item-info-field-defs';

// `ItemInfo` is re-typed here, using this app's own React 18 `FC`, rather than
// used directly from `@cheqroom/components` — that package resolves its own, package-local React 19
// `@types/react` (`packages/components/node_modules/@types/react`), independent of the root-hoisted
// 18.3.x this app and `packages/ui` share. Its `ReactNode` also permits `bigint`/`Promise<ReactNode>`,
// not assignable to this app's React 18 `ReactNode`, so every JSX call site fails with "'ItemInfo'
// cannot be used as a JSX component" otherwise — same fix `packages/ui`'s `Avatar`/`Icon` already use
// for the same cross-workspace type-identity issue (see `packages/ui/src/components/Avatar/Avatar.tsx`).
// Kept local to this app, not in `packages/ui`, since `ItemInfo` is a domain component, not a primitive.
// `ItemInfoItem` is reused directly — it's plain data (`Pick<Item, ...>`, no `ReactNode`/component
// reference), so it carries none of the cross-workspace taint the component values above do.
// `ItemInfoField` is reused directly too: it only flows in (as `extraFields`), and this app's React 18
// elements are assignable to the package's `ReactNode` — only the reverse direction fails.

export type { ItemInfoItem };

interface BaseProps extends StackProps {
	extraFields?: ItemInfoField[];
}

/** The caller has the item and decides which fields show. */
interface ItemProps extends BaseProps {
	item?: ItemInfoItem;
	fieldDefs?: ItemFieldEntry[];
	isLoading?: boolean;
	itemId?: never;
}

/** Only the id: the item is fetched, and the fields are the user's inline-field choice for its type. */
interface ItemIdProps extends BaseProps {
	itemId: string;
	/** A batched fetch already holding this item (e.g. one per page); skips this component's own request. */
	itemFields?: UseItemFieldsHook;
	/** Configured fields not to auto-render, e.g. `flag` when the caller shows its own flag. */
	excludeFieldIds?: string[];
	item?: never;
	fieldDefs?: never;
	isLoading?: never;
}

export type Props = ItemProps | ItemIdProps;

export interface FlagProps {
	showColor?: boolean;
	flag?: string;
}

export interface QRCodeProps {
	codes: string[];
}

export interface BarcodeProps {
	barcodes: string[];
}

interface ComponentComposition {
	Status: FC;
	Flag: FC<FlagProps>;
	PartOfKit: FC;
	Fields: FC;
	QRCode: FC<QRCodeProps>;
	Barcode: FC<BarcodeProps>;
}

const TypedItemInfo = SharedItemInfo as unknown as FC<PropsWithChildren<ItemProps>> & ComponentComposition;

type ConfiguredItemInfoProps = Omit<ItemIdProps, 'itemId' | 'itemFields' | 'item' | 'isLoading'> & {
	item: ItemInfoItem | null;
	isLoading: boolean;
};

/** The configured inline fields for an item that is already fetched (or being fetched) elsewhere. */
const ConfiguredItemInfo: FC<PropsWithChildren<ConfiguredItemInfoProps>> = ({
	item,
	isLoading,
	excludeFieldIds,
	children,
	...rest
}) => {
	const [configuredFieldIds] = useItemFieldsConfig(item?.itemTypeId);
	const fieldIds = useMemo(
		() => configuredFieldIds.filter((fieldId) => !excludeFieldIds?.includes(fieldId)),
		[configuredFieldIds, excludeFieldIds]
	);
	const fieldDefs = useItemInfoFieldDefs(item?.itemTypeId, fieldIds);

	return (
		<TypedItemInfo {...rest} item={item ?? undefined} fieldDefs={fieldDefs} isLoading={isLoading}>
			{children}
		</TypedItemInfo>
	);
};

/** A single item without a batched fetch: the only place `useItemFields` runs for this component. */
const FetchedItemInfo: FC<PropsWithChildren<Omit<ItemIdProps, 'itemFields'>>> = ({ itemId, ...rest }) => {
	const itemIds = useMemo(() => [itemId], [itemId]);
	const { getOriginalFields, isLoading } = useItemFields(itemIds);

	return (
		<ConfiguredItemInfo {...rest} item={getOriginalFields(itemId) as ItemInfoItem | null} isLoading={isLoading} />
	);
};

const WebItemInfo: FC<PropsWithChildren<Props>> = (props) => {
	if (props.itemId === undefined) return <TypedItemInfo {...props} />;

	const { itemId, itemFields, ...rest } = props;
	if (itemFields) {
		return (
			<ConfiguredItemInfo
				{...rest}
				item={itemFields.getOriginalFields(itemId) as ItemInfoItem | null}
				isLoading={itemFields.isLoading}
			/>
		);
	}

	return <FetchedItemInfo itemId={itemId} {...rest} />;
};

// A new object, so the shared component (mobile uses it too) is not mutated.
export const ItemInfo: FC<PropsWithChildren<Props>> & ComponentComposition = Object.assign(WebItemInfo, {
	Status: TypedItemInfo.Status,
	Flag: TypedItemInfo.Flag,
	PartOfKit: TypedItemInfo.PartOfKit,
	Fields: TypedItemInfo.Fields,
	QRCode: TypedItemInfo.QRCode,
	Barcode: TypedItemInfo.Barcode,
});

export { selectFieldDefs };
